import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Phone, Mail, CheckCircle, Shield, 
  Percent, Calendar, DollarSign, FileText,
  CreditCard, Building2
} from 'lucide-react';

const CashLoanPortal = ({ onBackToHome }) => {
  const [loanAmount, setLoanAmount] = useState(2000);
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // New loan calculation logic
  const namfisaFee = loanAmount * 0.0103;
  const interestBasis = loanAmount + namfisaFee;
  const interest = interestBasis * 0.29;
  const totalRepayment = interestBasis + interest;

  const requirements = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: 'Valid ID',
      description: 'Namibian ID or passport copy'
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: 'Latest Payslip',
      description: 'Most recent salary statement'
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: 'Bank Statement',
      description: 'Latest 3 months transaction history.'
    }
  ];

  const features = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'NAMFISA Registered',
      description: 'Fully regulated and compliant. Registration number: 25/11/921'
    },
    {
      icon: <Percent className="w-8 h-8" />,
      title: '29% Interest Rate',
      description: 'Competitive and transparent pricing'
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: '100% Online Application',
      description: 'The entire loan application process can be completed 100% online.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sticky Header */}
      <header className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button 
                onClick={onBackToHome}
                className="flex items-center space-x-1 sm:space-x-2 text-black hover:text-blue-600 transition-colors group"
              >
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium hidden sm:inline">Back</span>
              </button>
              <div className="w-px h-6 bg-gray-300 hidden sm:block"></div>
              <div className="flex items-center space-x-2 sm:space-x-3">
                <img 
                  src="/cash-loan-icon-logo2.jpeg" 
                  alt="Esh-Ham Cash Loans Logo" 
                  className="h-10 w-auto object-contain"
                />
                <div className="hidden sm:block">
                  <h1 className="text-lg font-bold text-black">Cash Loans</h1>
                  <p className="text-sm text-gray-600">Quick & Easy Financing</p>
                </div>
              </div>
            </div>
            
            <nav className="hidden lg:flex items-center space-x-8">
              <a href="#qualify" className="text-black hover:text-blue-600 font-medium transition-colors">How to Qualify</a>
              <a href="#calculator" className="text-black hover:text-blue-600 font-medium transition-colors">Calculator</a>
              <a href="#contact" className="text-black hover:text-blue-600 font-medium transition-colors">Contact</a>
            </nav>
            
            <div className="flex items-center">
              <a href="tel:+264814650599" className="flex items-center space-x-1 sm:space-x-2 text-black hover:text-blue-600 transition-colors">
                <Phone className="w-4 h-4" />
                <span className="text-xs sm:text-sm font-medium hidden xs:inline">+264 81 465 0599</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">Quick Cash Loans</span>
              <br />
              <span className="text-black">When You Need It Most</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Competitive rates and transparent terms, payable on your next pay day.
            </p>
          </div>
        </div>
      </section>
      
      {/* Divider */}
      <div className="h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent my-4"></div>

      {/* How to Qualify Section */}
      <section id="qualify" className="py-10 sm:py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4">How to Qualify</h3>
            <p className="text-lg text-blue-600">Three simple requirements to get started</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {requirements.map((req, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl shadow-lg p-8 text-center"
              >
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <div className="text-blue-600">{req.icon}</div>
                </div>
                <h4 className="text-xl font-bold text-black mb-2">{req.title}</h4>
                <p className="text-base text-gray-600">{req.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Apply Now CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl font-bold text-white mb-6">Ready to Apply?</h3>
          <p className="text-xl text-white/90 mb-8">
            Contact us today to start your loan application
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="tel:+264814650599"
              className="w-full sm:w-auto bg-white text-blue-600 font-bold py-4 px-8 rounded-lg text-lg transition-transform transform hover:scale-105 shadow-xl flex items-center justify-center space-x-3"
            >
              <Phone className="w-6 h-6" />
              <span>Call +264 81 465 0599</span>
            </a>
            <a 
              href="mailto:windhoek@eshham.com"
              className="w-full sm:w-auto bg-black/20 text-white font-bold py-4 px-8 rounded-lg text-lg transition-transform transform hover:scale-105 shadow-xl flex items-center justify-center space-x-3"
            >
              <Mail className="w-6 h-6" />
              <span>windhoek@eshham.com</span>
            </a>
          </div>
        </div>
      </section>

      {/* Loan Calculator Section */}
      <section id="calculator" className="py-10 sm:py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4">Loan Calculator</h3>
            <p className="text-lg text-blue-600">See your estimated pay-day repayment.</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12">
            <div className="mb-8">
              <label className="block text-lg font-semibold text-black mb-4">
                Loan Amount: <span className="text-blue-600">N$ {loanAmount.toLocaleString()}</span>
              </label>
              <input 
                type="range" 
                min="500" 
                max="5000" 
                step="100"
                value={loanAmount}
                onChange={(e) => setLoanAmount(Number(e.target.value))}
                className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <div className="flex justify-between text-sm text-gray-500 mt-2">
                <span>N$ 500</span>
                <span>N$ 5,000</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 md:gap-6 mb-8">
                <div className="bg-gray-100 p-4 md:p-6 rounded-xl text-center">
                    <p className="text-sm text-gray-600 mb-1">Loan Term</p>
                    <p className="text-3xl font-bold text-black">1</p>
                    <p className="text-xs text-gray-500">Month</p>
                </div>
                <div className="bg-gray-100 p-4 md:p-6 rounded-xl text-center">
                    <p className="text-sm text-gray-600 mb-1">Interest Rate</p>
                    <p className="text-3xl font-bold text-blue-600">29%</p>
                    <p className="text-xs text-gray-500">per annum</p>
                </div>
            </div>

            <div className="bg-gray-100 p-6 rounded-xl">
              <div className="border-t border-gray-200 pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-black">Total Repayment:</span>
                  <span className="text-xl font-bold text-blue-600">N$ {totalRepayment.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-500 text-center mt-6">
              * This is an estimate only. Actual amounts may vary based on your application.
            </p>
          </div>
        </div>
      </section>
      
      {/* Divider */}
      <div className="h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent my-4"></div>

      {/* Secure & Fast Section */}
      <section className="py-10 sm:py-12 md:py-16 px-4 sm:px-6 lg:px-8 bg-gray-100 relative overflow-hidden isolate">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-purple-600 to-blue-600 opacity-15 rounded-full translate-x-1/2 z-[-1] pointer-events-none"></div>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <h3 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4">Secure & Fast</h3>
            <p className="text-lg text-blue-600">Why choose Esh-Ham Cash Loans</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl shadow-lg p-8 text-center"
              >
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <div className="text-blue-600">{feature.icon}</div>
                </div>
                <h4 className="text-xl font-bold text-black mb-2">{feature.title}</h4>
                <p className="text-base text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Footer Section */}
      <section id="contact" className="py-10 sm:py-12 md:py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            <div>
              <h4 className="font-semibold mb-4 text-xl flex items-center">
                <Phone className="w-5 h-5 text-blue-600 mr-2" />
                Contact Us
              </h4>
              <div className="space-y-3">
                <a href="tel:+264814650599" className="flex items-center space-x-3 hover:text-blue-600 transition-colors">
                  <span className="text-base">+264 81 465 0599</span>
                </a>
                <a href="mailto:windhoek@eshham.com" className="flex items-center space-x-3 hover:text-blue-600 transition-colors">
                  <span className="text-base">windhoek@eshham.com</span>
                </a>
                <div className="mt-4 text-gray-400 text-base">
                  <p>Southern Industrial Area</p>
                  <p>Armstrong Street, Old Power Station, Office #36</p>
                  <p>Windhoek, Namibia</p>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-xl flex items-center">
                <DollarSign className="w-5 h-5 text-blue-600 mr-2" />
                Loan Information
              </h4>
              <div className="space-y-2 text-gray-400 text-base">
                <p className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span>29% Interest Rate</span>
                </p>
                <p className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span>Loans from N$ 500 - N$ 5,000</span>
                </p>
                <p className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span>1-month repayment term</span>
                </p>
                <p className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span>NAMFISA Registered</span>
                </p>
              </div>
            </div>
            
            <div className="sm:col-span-2 md:col-span-1">
              <h4 className="font-semibold mb-4 text-xl">Apply Now</h4>
              <p className="text-gray-400 mb-4 text-base">
                Ready to get the cash you need? Contact us today to start your application.
              </p>
              <a 
                href="https://wa.me/264814650599"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold py-3 px-6 rounded-lg text-base transition-transform transform hover:scale-105 shadow-lg"
              >
                Apply Now
              </a>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
            <p>&copy; 2025 Esh-Ham Cash Loans. All rights reserved. | NAMFISA Registered Micro-Lender</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CashLoanPortal;